import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAnalytics } from "@/hooks/useAnalytics";

import { useAuthStore } from "@/stores/authStore";
import { PencomConstants } from "@/constants/PencomConstants";
import { toast } from "@/utils/toast";
import type { User } from "@/models/responses/auth/user.response";
import {
  buildEmployerGroupProperties,
  buildEmployerPersonProperties,
  eventNames,
} from "@/utils/analytics";

const AuthListener = () => {
  const queryClient = useQueryClient();
  const { posthog, trackEvent } = useAnalytics();

  useEffect(() => {
    const unsubscribe = useAuthStore.subscribe((state, prevState) => {
      const currentEmployer = state.currentEmployer as User | null;

      if (
        state.isAuthenticated &&
        !prevState.isAuthenticated &&
        currentEmployer
      ) {
        const distinctId = currentEmployer.user?.id || currentEmployer.id;

        posthog?.identify(
          distinctId,
          buildEmployerPersonProperties(currentEmployer),
        );

        posthog?.group(
          "employer",
          currentEmployer.id,
          buildEmployerGroupProperties(currentEmployer),
        );

        trackEvent(eventNames.loginSuccess, {
          employerCode: currentEmployer.employerCode,
          employerType: currentEmployer.employerType,
          sector: currentEmployer.sector,
        });
      }

      if (!state.isAuthenticated && prevState.isAuthenticated) {
        // Clear all React Query cache on logout
        queryClient.clear();

        trackEvent(eventNames.logout, {
          intentional: state.isIntentionalLogout,
        });

        // Reset PostHog identity on logout
        posthog?.reset();

        if (!state.isIntentionalLogout) {
          toast.error(PencomConstants.AUTH_ERROR_MSG);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [posthog, queryClient]);

  return <></>;
};

export default AuthListener;
